import { combineReducers, compose } from 'redux';
import noop from 'lodash-es/noop';
import uniqueId from 'lodash-es/uniqueId';
import { configureStore, createAction } from '@reduxjs/toolkit';
import { reduxBatch } from '@manaflair/redux-batch';

import { AppCreator } from './types';
import applyMiddleware from './applyMiddleware';

export const init = createAction<void>('init');
export const terminate = createAction<void>('terminate');

class Bus {
  subscribers: Function[] = [];

  getState;

  emit(e) {
    this.subscribers.forEach((handler) => handler(e));
  }

  subscribe(handler) {
    this.subscribers.push(handler);

    return () => {
      this.subscribers = this.subscribers.filter((h) => h !== handler);
    };
  }

  setGetState(func) {
    this.getState = func;
  }
}

export const crossAppEventsBus = new Bus();

const broadcast = (appId: string) => (event) =>
  crossAppEventsBus.emit({ event, appId });
const getGlobalState = () => crossAppEventsBus.getState();
const addBroadcast =
  (appId: string) =>
  (next) =>
  (...args) => ({
    ...next(...args),
    broadcast: broadcast(appId),
    getGlobalState,
  });

// @ts-ignore
const createApp: AppCreator = (
  modules,
  { middlewares = [], isGlobal = false } = {},
) => {
  const appId = uniqueId('app');
  const calledModules = modules.map((module) => module());
  const allMiddlewares = calledModules
    .reduce(
      (result, { middleware = [] }) => [...result, ...middleware],
      middlewares,
    )
    .sort((a, b) => b.priority - a.priority);
  const deps = calledModules.reduce(
    (result, { expose, name }) =>
      expose ? { ...result, [name]: expose } : result,
    {},
  );

  const addDeps =
    (next) =>
    (...args) => ({ ...next(...args), deps });

  const store = configureStore({
    reducer: combineReducers(
      calledModules.reduce(
        (result, module) =>
          module.reducer
            ? { ...result, [module.name]: module.reducer }
            : result,
        {},
      ),
    ),
    enhancers: (gDM) =>
      gDM().concat(
        compose(
          applyMiddleware(...allMiddlewares),
          addDeps,
          addBroadcast(appId),
          reduxBatch,
        ),
      ),
    middleware: (gDM) => gDM(),
  });

  const cleanup = isGlobal
    ? crossAppEventsBus.subscribe(({ event }) => store.dispatch(event))
    : noop;

  if (isGlobal) {
    crossAppEventsBus.setGetState(() => store.getState());
  }

  return {
    id: appId,
    run: () => store.dispatch(init()),
    api: calledModules.reduce(
      (
        result,
        module: {
          name: string;
          api: Record<string, (...args: any[]) => unknown>;
        },
      ) =>
        module.api
          ? {
              ...result,
              [module.name]: Object.entries(module.api).reduce(
                (acc, [key, value]) => ({
                  ...acc,
                  [key]: (payload) =>
                    value(payload, { ...store, broadcast: broadcast(appId) }),
                }),
                {},
              ),
            }
          : result,
      {},
    ),
    store,
    broadcast,
    terminate: () => {
      store.dispatch(terminate());
      cleanup();
    },
  };
};

export default createApp;
